import { Cause, Deferred, Effect, Exit, Fiber, Layer, Ref, Scope } from "effect";
import type { EditorDocumentIdentity } from "./documents-model.ts";
import { EditorDocuments } from "./documents-service.ts";
import { EditorProjects, type EditorProject, type EditorProjectFailure } from "./project-service.ts";
import { EditorWidgetIo } from "./widget-service.ts";
import { EditorViews, type EditorViewBinding, type EditorViewSession } from "./view-service.ts";
import { makeImmutableRegistry } from "../tabs/registry.ts";
export const EditorViewsLive = (root: Scope.Scope) => Layer.effect(EditorViews, Effect.gen(function* () {
  const projects = yield* EditorProjects, io = yield* EditorWidgetIo, documents = yield* EditorDocuments;
  const failures = yield* Ref.make<readonly Cause.Reason<never>[]>([]);
  const retain = (cause: Cause.Cause<never>) => Ref.update(failures, current => [...current, ...cause.reasons.filter(reason => !current.includes(reason))]);
  yield* Effect.addFinalizer(() => Ref.get(failures).pipe(Effect.flatMap(reasons => reasons.length ? Effect.failCause(Cause.fromReasons(reasons)) : Effect.void)));
  const views = yield* makeImmutableRegistry<EditorViewSession, EditorProjectFailure>(root, retain);
  return EditorViews.of({
    open: Effect.fn("Frontend.EditorViews.open")(function* (key, container, callbacks, initial) {
      const lease = yield* views.acquire({
        key, contents: "", create: Effect.gen(function* () {
          const owner = yield* Scope.Scope, widgetScope = yield* Scope.fork(owner);
          const widget = yield* Ref.make<import("./widget-service.ts").EditorWidgetHandle | undefined>(undefined);
          const priming = yield* Ref.make<Scope.Closeable | undefined>(undefined);
          const selected = yield* Ref.make<{
            readonly binding: object;
            readonly request: object;
          } | undefined>(undefined);
          const bindings = yield* makeImmutableRegistry<EditorViewBinding, EditorProjectFailure>(root, retain);
          // The widget saves view state and calls onUnmount while its models are live.
          // Then the binding registry closes every joined project borrower.
          const bindingKey = (target: EditorDocumentIdentity) => JSON.stringify([target.workspaceId, target.generation]);
          const reserve = Effect.fn("Frontend.EditorViews.reserve")(function* (target: EditorDocumentIdentity) {
            const lease = yield* bindings.acquire({
              key: bindingKey(target), contents: "", create: Effect.uninterruptibleMask(restore => Effect.gen(function* () {
                const bindingScope = yield* Scope.Scope, ready = yield* Deferred.make<Exit.Exit<EditorProject, EditorProjectFailure>>(), release = yield* Deferred.make<void>();
                const work = projects.use(target, project => Deferred.succeed(ready, Exit.succeed(project)).pipe(Effect.andThen(Deferred.await(release)))).pipe(Effect.onExit(exit => Exit.isFailure(exit) ? Deferred.succeed(ready, Exit.failCause(exit.cause)).pipe(Effect.asVoid) : Effect.void));
                const borrower = yield* Effect.forkIn(work, bindingScope);
                yield* Effect.addFinalizer(() => Deferred.succeed(release, undefined).pipe(Effect.andThen(Fiber.interrupt(borrower)), Effect.asVoid));
                const project = yield* restore(Deferred.await(ready).pipe(Effect.flatMap(exit => exit)));
                const binding: EditorViewBinding = { identity: {}, target, project };
                yield* Effect.addFinalizer(() => Effect.gen(function* () {
                  const view = yield* documents.read;
                  const current = () => owner.state._tag !== "Closed" && root.state._tag !== "Closed" && Ref.getUnsafe(selected)?.binding === binding.identity;
                  const editor = yield* Ref.get(widget);
                  if (editor) yield* io.bind(editor, undefined, view.files, callbacks, current).pipe(Effect.orDie);
                }));
                return binding;
              }))
            });
            return lease.model;
          });
          const initialScope = initial ? yield* Scope.fork(owner) : undefined;
          if (initialScope) yield* Ref.set(priming, initialScope);
          const initialBinding = initial && initialScope
            ? yield* reserve(initial.target).pipe(Effect.provideService(Scope.Scope, initialScope))
            : undefined;
          const initialModel = initialBinding && initial?.currentFile
            ? yield* initialBinding.project.model(initial.currentFile)
            : undefined;
          if (initialBinding) yield* Ref.set(selected, { binding: initialBinding.identity, request: {} });
          yield* Effect.addFinalizer(() => Scope.close(widgetScope, Exit.void));
          const editor = yield* io.acquire(container, callbacks, initialModel).pipe(Effect.provideService(Scope.Scope, widgetScope));
          yield* Ref.set(widget, editor);
          const bind = Effect.fn("Frontend.EditorViews.bind")(function* (target: EditorDocumentIdentity) {
            const binding = yield* reserve(target);
            const held = yield* Ref.getAndSet(priming, undefined);
            if (held) yield* Scope.close(held, Exit.void);
            return binding;
          });
          const update = Effect.fn("Frontend.EditorViews.update")(function* (binding: EditorViewBinding, currentFile: string | undefined) {
            if (owner.state._tag === "Closed" || root.state._tag === "Closed")
              return yield* Effect.interrupt;
            const request = {}, selection = { binding: binding.identity, request };
            yield* Ref.set(selected, selection);
            const current = () => owner.state._tag !== "Closed" && root.state._tag !== "Closed" && Ref.getUnsafe(selected) === selection;
            const view = yield* documents.read;
            if (view.closed || bindingKey(view) !== bindingKey(binding.target))
              return yield* Effect.interrupt;
            yield* binding.project.synchronize;
            const model = currentFile ? yield* binding.project.model(currentFile) : undefined;
            yield* io.bind(editor, model, view.files, callbacks, current);
          });
          return { bind, update } satisfies EditorViewSession;
        })
      });
      return lease.model;
    })
  });
}));
